import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { LogOut, ShieldCheck, UserCog, ChevronDown, User, History, Database, AlertTriangle, Compass } from "lucide-react";
import { useAuth, PERMS } from "../lib/auth";
import UserManagementModal from "./MobileUserManagementModal";
import DoctorsManager from "./DoctorsManager";
import SchoolsManager from "./SchoolsManager";
import SquadsManager from "./SquadsManager";
import ClassesManager from "./ClassesManager";
import LevelsManager from "./LevelsManager";
import BloodGroupsManager from "./BloodGroupsManager";
import BackupRestoreModal from "./BackupRestoreModal";
import ParamRangesModal from "./ParamRangesModal";
import DataQualityModal from "./DataQualityModal";
import FeatureGuideModal, { shouldAutoShowFeatureGuide } from "./FeatureGuideModal";
import BulkBatchesModal from "./BulkBatchesModal";
import BulkDewormingModal from "./BulkDewormingModal";
import BulkSupplementModal from "./BulkSupplementModal";
import BulkAssignSquadClassModal from "./BulkAssignSquadClassModal";
import BulkLabImportWizard from "./BulkLabImportWizard";
import AttendanceSyncModal, { AttendanceSyncMenuItem } from "./AttendanceSyncModal";

export default function UserMenu() {
  const { user, logout, isAdmin, hasPerm, authEnabled } = useAuth();
  const [open, setOpen] = useState(false);
  const [showUsers, setShowUsers] = useState(false);
  // Archive (edits & deletions) is no longer mounted here. The event
  // `menu:audit` is forwarded to the Activity Log tab which hosts the
  // ArchiveModal natively.
  const [showDoctors, setShowDoctors] = useState(false);
  const [showSchools, setShowSchools] = useState(false);
  const [showSquads, setShowSquads] = useState(false);
  const [showClasses, setShowClasses] = useState(false);
  const [showLevels, setShowLevels] = useState(false);
  const [showBloodGroups, setShowBloodGroups] = useState(false);
  // showArchive removed — Archive (edits & deletions) is now a toggle
  // inside the Activity Log tab via the `menu:audit` event dispatch.
  const [showBackup, setShowBackup] = useState(false);
  const [showRanges, setShowRanges] = useState(false);
  const [showDQ, setShowDQ] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [showBulkBatches, setShowBulkBatches] = useState(false);
  const [showAttendanceSync, setShowAttendanceSync] = useState(false);
  // These bulk modals moved from DataEditor's local state to here so
  // they can be opened from the top nav's Bulk group as well as from
  // the Data Editor toolbar. UserMenu is always mounted, so it's the
  // right home for singleton modals that need to be reachable from
  // anywhere in the app.
  const [showBulkDeworming, setShowBulkDeworming] = useState(false);
  const [showBulkSupplement, setShowBulkSupplement] = useState(false);
  const [showBulkSquad, setShowBulkSquad] = useState(false);
  const [showBulkLabs, setShowBulkLabs] = useState(false);
  // Auto-open the feature guide once per browser for new users. Skips
  // the popup entirely if localStorage is unavailable (private tabs).
  useEffect(() => {
    if (shouldAutoShowFeatureGuide()) {
      // Small delay so the guide doesn't fight the login transition
      // + auth-check spinner for stacking-context focus.
      const t = setTimeout(() => setShowGuide(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  // Listen for deep-link "open this modal" events dispatched by the
  // in-app Help Assistant. Keeps the wiring loose — we don't have to
  // lift every modal's state up to App.js.
  useEffect(() => {
    const map = {
      "menu:bulk_batches":    () => setShowBulkBatches(true),
      "menu:bulk_deworming":  () => setShowBulkDeworming(true),
      "menu:bulk_supplement": () => setShowBulkSupplement(true),
      "menu:bulk_squad":      () => setShowBulkSquad(true),
      "menu:bulk_labs":       () => setShowBulkLabs(true),
      "menu:data_quality":    () => setShowDQ(true),
      "menu:schools":         () => setShowSchools(true),
      "menu:squads":          () => setShowSquads(true),
      "menu:classes":         () => setShowClasses(true),
      "menu:levels":          () => setShowLevels(true),
      "menu:blood_groups":    () => setShowBloodGroups(true),
      "menu:doctors":         () => setShowDoctors(true),
      "menu:ranges":          () => setShowRanges(true),
      "menu:audit":           () => {
        // Archive is now folded into the Activity Log tab. Jump the
        // user there and let the tab's own listener open the overlay.
        window.dispatchEvent(new CustomEvent("ych:switch-tab", { detail: "spreadsheet" }));
        setTimeout(() => window.dispatchEvent(new CustomEvent("ych:open", { detail: "menu:audit" })), 60);
      },
      "menu:backup":          () => setShowBackup(true),
      "menu:guide":           () => setShowGuide(true),
    };
    const handler = (e) => {
      const route = e?.detail?.route;
      const fn = map[route];
      if (fn) fn();
    };
    window.addEventListener("ych:open", handler);
    return () => window.removeEventListener("ych:open", handler);
  }, []);
  // Position the dropdown manually so it escapes any ancestor stacking context.
  const [pos, setPos] = useState({ top: 0, right: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    setPos({
      top: r.bottom + 8,
      right: Math.max(8, window.innerWidth - r.right),
    });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onResize = () => {
      if (!btnRef.current) return;
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 8, right: Math.max(8, window.innerWidth - r.right) });
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [open]);

  if (!user) return null;
  const perms = isAdmin ? PERMS : user.permissions || [];

  return (
    <>
      <button
        ref={btnRef}
        className="pill"
        onClick={() => setOpen((v) => !v)}
        data-testid="user-menu-btn"
        title={user.email}
      >
        <User size={14} />
        <span className="hidden sm:inline truncate max-w-[140px]">{user.name || user.email}</span>
        <ChevronDown size={12} />
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            className="fixed w-72 card p-3 shadow-2xl"
            style={{
              top: pos.top,
              right: pos.right,
              zIndex: 200,
              // Fully opaque surface — defeats any background bleed-through
              // from the hero banner / sticky header.
              background: "#0B1627",
            }}
            data-testid="user-menu-dropdown"
          >
            <div className="px-2 py-2 border-b border-[#1E304B] mb-2">
              <div className="text-sm font-display text-slate-100 truncate">{user.name || user.email}</div>
              <div className="text-[11px] text-slate-400 font-mono-data truncate">{user.email}</div>
              <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                {authEnabled === false && (
                  <span
                    className="px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider bg-rose-500/15 border border-rose-500/40 text-rose-200"
                    title="Login is disabled on the backend (AUTH_ENABLED=false)"
                  >
                    Auth disabled
                  </span>
                )}
                {isAdmin ? (
                  <span className="px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider bg-amber-500/15 border border-amber-500/40 text-amber-200 flex items-center gap-0.5">
                    <ShieldCheck size={10} /> Admin
                  </span>
                ) : (
                  perms.map((p) => (
                    <span
                      key={p}
                      className="px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider bg-cyan-500/10 border border-cyan-500/30 text-cyan-200 font-mono-data"
                    >
                      {p}
                    </span>
                  ))
                )}
                {!isAdmin && perms.length === 0 && (
                  <span className="text-[10px] text-slate-500 italic">no permissions</span>
                )}
              </div>
            </div>
            {isAdmin && (
              <button
                className="w-full text-left px-2 py-2 rounded-md hover:bg-[#0E1A2E] text-sm text-slate-200 flex items-center gap-2"
                onClick={() => {
                  setOpen(false);
                  setShowUsers(true);
                }}
                data-testid="manage-users-btn"
              >
                <UserCog size={14} className="text-cyan-300" /> Manage users
              </button>
            )}
            {/* Archive (edits & deletions) is now folded into the
                Activity Log tab — one unified surface. Entry point
                remains via `menu:audit` for existing deep-links. */}
            <button
              className="w-full text-left px-2 py-2 rounded-md hover:bg-[#0E1A2E] text-sm text-slate-200 flex items-center gap-2"
              onClick={() => {
                setOpen(false);
                setShowGuide(true);
              }}
              data-testid="feature-guide-btn"
            >
              <Compass size={14} className="text-cyan-300" /> What&apos;s in this app
            </button>
            {/* Schools / Squads / Classes / Levels / Doctors / Reference ranges
                (previously listed here) moved to the top-nav "Masters ▾" group
                so they're first-class navigation, not buried in the Avatar
                dropdown (2026-02-15 UX consolidation).
                "Bulk batches" and the new-batch actions (deworming / supplement
                / squad reassign) moved to the top-nav "Bulk ▾" group for the
                same reason. Modals themselves still live at the bottom of this
                file so they can be opened from any tab via the `ych:open`
                event bus. */}
            {isAdmin && (
              <AttendanceSyncMenuItem
                onClick={() => {
                  setOpen(false);
                  setShowAttendanceSync(true);
                }}
              />
            )}
            <button
              className="w-full text-left px-2 py-2 rounded-md hover:bg-[#0E1A2E] text-sm text-slate-200 flex items-center gap-2"
              onClick={() => {
                setOpen(false);
                setShowBackup(true);
              }}
              data-testid="backup-restore-btn"
            >
              <Database size={14} className="text-cyan-300" /> Backup &amp; restore
            </button>
            {(isAdmin || hasPerm("edit")) && (
              <button
                className="w-full text-left px-2 py-2 rounded-md hover:bg-[#0E1A2E] text-sm text-slate-200 flex items-center gap-2"
                onClick={() => {
                  setOpen(false);
                  setShowDQ(true);
                }}
                data-testid="data-quality-btn"
              >
                <AlertTriangle size={14} className="text-rose-300" /> Data quality check
              </button>
            )}
            {authEnabled !== false && (
              <button
                className="w-full text-left px-2 py-2 rounded-md hover:bg-[#0E1A2E] text-sm text-rose-300 flex items-center gap-2"
                onClick={() => {
                  setOpen(false);
                  logout();
                }}
                data-testid="logout-btn"
              >
                <LogOut size={14} /> Sign out
              </button>
            )}
          </div>,
          document.body,
        )}
      <UserManagementModal open={showUsers} onClose={() => setShowUsers(false)} />
      <DoctorsManager open={showDoctors} onClose={() => setShowDoctors(false)} />
      <SchoolsManager open={showSchools} onClose={() => setShowSchools(false)} />
      <SquadsManager open={showSquads} onClose={() => setShowSquads(false)} />
      <ClassesManager open={showClasses} onClose={() => setShowClasses(false)} />
      <LevelsManager open={showLevels} onClose={() => setShowLevels(false)} />
      <BloodGroupsManager open={showBloodGroups} onClose={() => setShowBloodGroups(false)} />
      <FeatureGuideModal open={showGuide} onClose={() => setShowGuide(false)} />
      <BackupRestoreModal open={showBackup} onClose={() => setShowBackup(false)} />
      <ParamRangesModal open={showRanges} onClose={() => setShowRanges(false)} />
      <DataQualityModal open={showDQ} onClose={() => setShowDQ(false)} />
      <BulkBatchesModal open={showBulkBatches} onClose={() => setShowBulkBatches(false)} />
      <AttendanceSyncModal open={showAttendanceSync} onClose={() => setShowAttendanceSync(false)} />
      {/* Singleton bulk modals — hoisted from DataEditor's local state to
          here so they're openable from the top-nav "Bulk ▾" group as
          well as from the Data Editor toolbar. Sibling views (Data
          Editor, Schedule) dispatch `ych:data-changed` on save via the
          axios interceptor, so we don't need an explicit onSaved
          callback here. */}
      <BulkDewormingModal
        open={showBulkDeworming}
        onClose={() => setShowBulkDeworming(false)}
      />
      <BulkSupplementModal
        open={showBulkSupplement}
        onClose={() => setShowBulkSupplement(false)}
      />
      <BulkAssignSquadClassModal
        open={showBulkSquad}
        onClose={() => setShowBulkSquad(false)}
      />
      <BulkLabImportWizard
        open={showBulkLabs}
        onClose={() => setShowBulkLabs(false)}
        onCommitted={() => {
          // Fan out `ych:data-changed` so App.js reloads the sailor
          // list + squad stats and DataSpreadsheetView refreshes its
          // rows. Same signal every mutation-side view already listens
          // on (see axios interceptor in `lib/api.js`).
          window.dispatchEvent(new CustomEvent("ych:data-changed"));
        }}
      />
    </>
  );
}
